<template>
  <div class="left-column">
    <div class="image-goal md:mr-4 xl:mr-0">
      <div class="fundraiser-image">
        <img
          :src="fundraiser.heroLink"
          alt="Hero Image"
          data-test="hero-image"
        />
      </div>
      <div style="width: 280px">
        <div class="amount-raised">
          <span
            class="font-22 weight-700"
            data-test="amount-raised"
            >{{ getAmount(fundraiser.fundraisers_id) }}</span
          >
          <span
            class="goal"
            data-test="goal"
          >
            of {{ getGoal(fundraiser.fundraisers_id, fundraiser.goal ? fundraiser.goal : 0) }} Goal
          </span>
        </div>
        <div class="progress-bar mb-4">
          <div
            :style="{
              width: calculateWidth(fundraiser),
              background: donorStore.mainColor,
              height: '100%',
              borderRadius: 'inherit',
            }"
            data-test="progress-bar"
          ></div>
        </div>
      </div>
    </div>
    <div>
      <div style="display: flex; justify-content: space-between; margin: 0 0 8px">
        <div class="flex grow">
          <Button
            class="p-button-outlined p-button flex-grow mr-1"
            :style="{ justifyContent: 'center', color: '#000' }"
            @click="edit"
            :data-test="'edit-button-' + fundraiser.fundraisers_id"
            :disabled="fundraiser.donorAccounts_id != donorStore.donorAccounts_id"
          >
            <img
              style="padding-right: 5px; color: black"
              :src="PencilBlack"
            />Edit Page
          </Button>
        </div>
        <div class="flex grow">
          <Button
            class="p-button-outlined p-button flex grow ml-1"
            :style="{ color: '#000' }"
            @click="openFundraiserPage"
            :data-test="'visit-button-' + fundraiser.fundraisers_id"
          >
            <img
              style="padding-right: 5px"
              :src="Eye"
            />Visit Page
          </Button>
        </div>
      </div>
      <div style="margin: 16px 0">
        <Button
          v-if="isMobile"
          class="p-button-outlined p-button w-full"
          :style="{ color: '#000' }"
          @click="shareMobile"
          :data-test="'share-mobile-button-' + fundraiser.fundraisers_id"
        >
          <img
            style="padding-right: 5px"
            :src="Share"
          />Share
        </Button>
        <Button
          v-else
          class="p-button-outlined p-button w-full"
          :style="{ color: '#000' }"
          @click="shareDetails = true"
          :data-test="'share-button-' + fundraiser.fundraisers_id"
        >
          <img
            style="padding-right: 5px"
            :src="Share"
          />Share
        </Button>
      </div>
      <div style="height: 48px; margin: 16px 0">
        <Button
          :disabled="fundraiser.status === 'Deactivated' || fundraiser.status === 'Expired'"
          class="p-button-outlined p-button w-full text-black"
          :style="{ color: '#000' }"
          @click="deactivate"
          :loading="fundraiserStore.isLoading"
          :data-test="'deactivate-button-' + fundraiser.fundraisers_id"
        >
          <img
            v-if="!fundraiserStore.isLoading"
            :class="{ 'disabled-image': fundraiser.status === 'Deactivated' || fundraiser.status === 'Expired' }"
            style="padding-right: 5px"
            :src="Deactivate"
          />Deactivate Fundraiser
        </Button>
      </div>
    </div>
    <ShareModal
      :show="shareDetails"
      @close="shareDetails = false"
      :data-test="'share-modal-' + fundraiser.fundraisers_id"
    />
    <FundraiserCreation
      v-if="fundraiserStore.editFundraiser"
      :step="1"
      :data-test="'fundraiser-creation-' + fundraiser.fundraisers_id"
    ></FundraiserCreation>
  </div>
</template>

<script setup>
import { defineProps, ref, watch, onMounted } from 'vue';
import { useDonorStore } from '@/stores/DonorStore.js';
import { useFundraiserStore } from '@/stores/FundraiserStore.js';
import { useRoute } from 'vue-router';
import filters from '@/helpers/filters';
import PencilBlack from '@/assets/PencilBlack.svg';
import Share from '@/assets/Share.svg';
import Eye from '@/assets/Eye.svg';
import Deactivate from '@/assets/Deactivate.svg';
import ShareModal from './ShareModal.vue';
import useIsMobile from '@/composables/useIsMobile.js';
import FundraiserCreation from '@/pages/donorAccount/fundraisers/FundraiserCreation.vue';

const donorStore = useDonorStore();
const fundraiserStore = useFundraiserStore();
const { isMobile } = useIsMobile();

const route = useRoute();
const shareDetails = ref(false);

const fundraiserId = parseInt(route.params.idOrName, 10);
const fundraiser = donorStore.getFundraiserById(fundraiserId);
let fullUrl = '';

if (fundraiser) {
  fullUrl = fundraiser.domain.replace(/\/$/, '') + '/fundraiser/' + fundraiser.urlLink;
}

const amount = ref(0);
const goal = ref(0);
const modalStep = ref(1);

const props = defineProps({
  editOpen: Boolean,
});

onMounted(() => {
  if (props.editOpen && route.fullPath.includes('edit')) {
    edit();
  }
});

// share button
const shareMobile = async () => {
  if (navigator.share) {
    try {
      await navigator.share({
        title: 'Check this out!',
        text: 'Here is something interesting I found:',
        url: fullUrl,
      });
    } catch (error) {
      console.error('Error sharing content:', error);
    }
  } else {
    alert('Your browser does not support the Web Share API');
  }
};

// goal and amount
const getAmount = (id) => {
  if (fundraiser.status == 'published') {
    const data = fundraiserStore.fundraiserDonationData[id];
    if (!data) {
      return filters.formatMoney(0, 0);
    }
    amount.value = data.amount;
    return filters.formatMoney(data.amount, 0);
  } else {
    amount.value = fundraiser.amountRaised_cached;
    return filters.formatMoney(fundraiser.amountRaised_cached, 0);
  }
};

const getGoal = (id, cachedGoal) => {
  if (fundraiser.status == 'published') {
    const data = fundraiserStore.fundraiserDonationData[id];
    if (!data) {
      return filters.formatMoney(cachedGoal, 0);
    }
    goal.value = data.goal;
    return filters.formatMoney(data.goal, 0);
  } else {
    goal.value = fundraiser.goal;
    return filters.formatMoney(fundraiser.goal, 0);
  }
};

const calculateWidth = () => {
  const percentComplete = Math.min(amount.value / goal.value, 1); // Ensure it does not exceed 100%
  return `${percentComplete * 100}%`;
};

// view fundraiser page
const openFundraiserPage = () => {
  window.open(fullUrl, '_blank');
};

// deactivate fundraiser
const deactivate = async () => {
  await fundraiserStore
    .deactivateFundraiser(donorStore.donorInfo.donorAccounts_id, fundraiser.fundraisers_id)
    .then(() => {
      fundraiser.status = 'Deactivated';
    });
};

const removeCreatedQueryParam = () => {
  const urlWithoutQueryParam = window.location.href.replace('?created=true', '');
  window.history.replaceState({}, document.title, urlWithoutQueryParam);
};

watch(
  () => fundraiserStore.fundraiserUpdated,
  (updated) => {
    if (updated) {
      // Only update if the goal is different
      if (fundraiserStore.goal) {
        fundraiser.goal = fundraiserStore.goal; // convert to dollars
      }

      // Only update the title if it's different
      if (fundraiserStore.fundraiserTitle) {
        fundraiser.title = fundraiserStore.fundraiserTitle;
      }

      // Only update heroLink if it's different
      if (fundraiserStore.heroLink) {
        fundraiser.heroLink = fundraiserStore.heroLink;
      }

      // Only update the summary if it's different
      if (fundraiserStore.story) {
        fundraiser.summary = fundraiserStore.story;
      }
    }
  },
);

//edit fundraisers
const edit = async () => {
  removeCreatedQueryParam();
  modalStep.value = 1;
  fundraiserStore.success = false;
  fundraiserStore.editFundraiser = true;
  fundraiserStore.fundraiserTitle = fundraiser.title;
  fundraiserStore.benefittingOrgHostsId = fundraiser.organizations_hosts_id;
  fundraiserStore.benefittingOrgName = fundraiser.associatedOrgName;
  fundraiserStore.urlLink = fundraiser.urlLink;
  fundraiserStore.creatorName = donorStore.donorInfo.firstName + ' ' + donorStore.donorInfo.lastName;
  fundraiserStore.goal = fundraiser.goal;
  fundraiserStore.heroLink = fundraiser.heroLink;
  fundraiserStore.story = fundraiser.summary;
  fundraiserStore.domain = fundraiser.domain;
  fundraiserStore.fundraisers_id = fundraiser.fundraisers_id;
  fundraiserStore.companyGiving = fundraiser.companyGiving;
};
</script>
<style lang="scss" scoped>
.disabled-image {
  filter: grayscale(100%);
  opacity: 0.5;
  pointer-events: none; /* Prevent interaction */
}

.left-column {
  display: flex;
  flex-direction: column;
  margin: 32px 12px 32px 0;
}

.image-goal {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.fundraiser-image {
  height: 144px;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  overflow: hidden;
  border-radius: 8px;
}

.fundraiser-image img {
  vertical-align: middle;
  max-height: 100%;
  width: 100%;
  object-fit: cover;
}

.progress-bar {
  height: 8px;
  flex-shrink: 0;
  border-radius: 4px;
  background: var(--Purple-50, #e6e9ee);
}
.amount-raised {
  color: #000;
  font-family: Poppins;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 25px;
  margin-top: 16px;
  margin-bottom: 8px;
}

@media (max-width: 1288px) {
  .left-column {
    margin: 32px 0;
    flex-direction: row;
  }
}

@media (max-width: 768px) {
  .left-column {
    flex-direction: column;
  }
}
</style>
